///
/// EarthBrowser
///
/// Created by Matt Giger on 1/19/2024
/// Copyright (c) 2024 Geodata Labs
///

import { engineActions, engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { cFreeHistoryHours, cPaidHistoryHours } from "@/utils/entitlementLimits"
import { requestRender } from "@stovokor/engine/RenderRequest"
import { time, timedelta, timerange } from "@stovokor/math/time"

const cEndOffset						= timedelta.hours(1)
const cHoursPerSecAnimation 			= 600 / 3600
// const cAnimationScale					= 3 * 60 * 60
const c_pause_seconds					= 1.0

export class Clock {
	public static time: time = new time()

	public clockTime: time = new time()
	private nextTime: time = new time()
	public clockState: "animating" | "paused" | "real-time" = "real-time"
	public animationSpeed: number = cHoursPerSecAnimation * 3600
	// private animationPercentage: number  = 0

	// The user's stored hours of history. The effective offset is clamped by
	// the entitlement tier (see clockStartOffset below), matching how the
	// TimelineBar clamps the persisted value at read time.
	private startOffsetHours: number = 36
	private paidEntitlement: boolean = isPaidEntitlement()
	private clockEndOffset: timedelta = cEndOffset
	private clockRange: timerange

	// Max speed imposed by the focused entity (e.g. satellite tracking).
	// The user's chosen speed is preserved; this only limits effective playback.
	private speedCap: number | null = null

	private lastSecondsSinceStart: number = 0
	private lastSeconds: number = 0
	private endPauseSeconds: number = 0
	private reduxUnsubscribers: (() => void)[] = []
	private isExternalUpdate: boolean = false

	constructor() {
		// set initial state from redux when a store exists (the embed build
		// has none: the defaults above give it a real-time clock)
		const state = engineBridge.getState()
		if (state) {
			this.clockState = state.earthbrowser.clockState
			this.animationSpeed = state.earthbrowser.clockAnimatingSpeed
			this.startOffsetHours = state.earthbrowser.clockStartOffset
			this.paidEntitlement = state.entitlements.isPaid
			this.clockEndOffset = timedelta.hours(state.earthbrowser.clockEndOffset)
		}

		const now = new time()
		this.clockRange = new timerange(now.adding(this.clockStartOffset), now.adding(this.clockEndOffset))

		this.lastSecondsSinceStart = performance.now() * 0.001

		// Subscribe to Redux camera control actions
		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<number>(engineActions.setClockCurrentTime, (payload: number) => {
				// Entitlement floor: external time sets (frame stepping, the
				// datetime editor, raw dispatches) must not reach past the
				// tier's history depth, or every time-based layer renders
				// unlicensed history. The UI gates its own controls; this is
				// the engine-side backstop.
				const capHours = this.paidEntitlement ? cPaidHistoryHours : cFreeHistoryHours
				const floorMs = Date.now() - capHours * 3600_000
				const clampedMs = Math.max(payload, floorMs)
				// Mark as external update to avoid dispatching back
				this.isExternalUpdate = true
				this.updateTimeValue(new time(new Date(clampedMs)))
				// Echo a meaningfully clamped time so the UI doesn't display
				// a time the engine refused (tolerance avoids re-dispatch
				// loops as Date.now() advances between dispatches)
				if (payload < floorMs - 1000) {
					engineBridge.dispatch({ type: engineActions.setClockCurrentTime, payload: clampedMs })
				}
			})
		)

		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<"animating" | "paused" | "real-time">(engineActions.setClockState, (payload: "animating" | "paused" | "real-time") => {
				// Play from LIVE starts at the beginning of the history
				// window rather than at "now": from now the whole loop runs
				// through the forecast window, where observation-only layers
				// (radar, IMERG) just hold their newest frame. Play from
				// paused keeps the displayed time - the user scrubbed there
				if (payload === "animating" && this.clockState === "real-time") {
					this.clockTime = this.nextTime = this.clockRange.start
					this.endPauseSeconds = 0
					engineBridge.dispatch({ type: engineActions.setClockCurrentTime, payload: this.clockTime.date.getTime() })
				}
				this.clockState = payload
			})
		)

		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<number>(engineActions.setClockAnimatingSpeed, (payload) => {
				this.animationSpeed = payload
			})
		)

		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<number | null>(engineActions.setClockSpeedCap, (payload) => {
				this.speedCap = payload
			})
		)

		// Keep the animation window in sync with the UI's time-range steppers,
		// so the animating clock wraps at the user's current end offset instead
		// of the one captured at startup
		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<number>(engineActions.setClockStartOffset, (payload) => {
				this.startOffsetHours = payload
			})
		)

		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<number>(engineActions.setClockEndOffset, (payload) => {
				this.clockEndOffset = timedelta.hours(payload)
			})
		)

		// Free tier caps the history window: without tracking this, looped
		// playback wraps to the persisted (possibly paid-depth) start while
		// the timeline UI displays the clamped one
		this.reduxUnsubscribers.push(
			engineBridge.subscribeAction<boolean>(engineActions.setPaidEntitlement, (payload) => {
				this.paidEntitlement = payload
			})
		)
	}

	// Start of the animation window: the persisted offset clamped to the
	// entitlement tier's history depth, mirroring TimelineBar's
	// effectiveStartOffset so the clock wraps where the UI says it will
	private get clockStartOffset(): timedelta {
		const capHours = this.paidEntitlement ? cPaidHistoryHours : cFreeHistoryHours
		return timedelta.hours(-Math.min(this.startOffsetHours, capHours))
	}

	/** The time window the clock animates within (wraps to start at end) */
	public get range(): timerange {
		return this.clockRange
	}

	public beginFrame(timestamp: time): void {

		const currentSeconds = performance.now() * 0.001
		const effectiveSpeed = this.speedCap !== null
			? Math.min(this.animationSpeed, this.speedCap)
			: this.animationSpeed
		const delta = effectiveSpeed * (currentSeconds - this.lastSecondsSinceStart)
		this.lastSecondsSinceStart = currentSeconds

		if(this.clockState === "paused") {
			// Handle external updates when paused
			if (this.isExternalUpdate) {
				this.clockTime = this.nextTime
				this.isExternalUpdate = false
				requestRender()		// a scrub changes what every time-driven layer shows
			}
			Clock.time = this.clockTime
			return
		}

		// Playback moves time-driven content (radar frames, isobar warp,
		// satellite positions) every tick; keep the engine drawing at full
		// rate rather than its idle floor while the clock is animating
		if(this.clockState === "animating") {
			requestRender()
		}

		this.clockRange.start = timestamp.adding(this.clockStartOffset)
		if(this.clockRange.start.greater(timestamp)) {
			this.clockRange.start = timestamp
		}

		this.clockRange.end = timestamp.adding(this.clockEndOffset)
		if(this.clockRange.end.less(timestamp)) {
			this.clockRange.end = timestamp
		}

		if(this.clockState === "real-time") {
			this.clockTime = this.nextTime = timestamp
		} else if(this.clockState === "animating") {

			if(this.endPauseSeconds > 0) {
				if(currentSeconds >= this.endPauseSeconds) {
					this.clockTime = this.nextTime = this.clockRange.start
					this.endPauseSeconds = 0
				}
				else {
					return
				}
			}

			const newTime = new time(this.clockTime.julian + delta / 86400.0)

			// reset range values on every loop in case it changes
			if(newTime.greater(this.clockRange.end)) {
				this.clockTime = this.nextTime = this.clockRange.end
				this.endPauseSeconds = currentSeconds + c_pause_seconds
			} else {
				this.clockTime = this.nextTime = newTime
			}
		}

		const date = this.clockTime.date
		const seconds = date.getUTCSeconds()
		if(this.lastSeconds != seconds) {
			this.lastSeconds = seconds
			// Dispatch time update to Redux for UI sync (once per second to avoid flooding)
			engineBridge.dispatch({ type: engineActions.setClockCurrentTime, payload: this.clockTime.date.getTime() })
		}

		// Clear external update flag
		this.isExternalUpdate = false

		Clock.time = this.clockTime
	}

	public updateTimeValue(time: time): void {
		this.nextTime = time
	}

	/**
	 * Pause the clock at an exact instant, bypassing the store (the embed
	 * has none) and the entitlement floor. For the first-party video render
	 * mode (src/embed/renderHooks.ts), which steps simulated time frame by
	 * frame; the new time takes effect at the next beginFrame
	 */
	public setPausedTime(ms: number): void {
		this.clockState = "paused"
		this.isExternalUpdate = true
		this.updateTimeValue(new time(new Date(ms)))
	}
}
